import { NgTemplateOutlet } from '@angular/common';
import { Component, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';

/**
 * Formly field type that renders a grid of clickable "card" buttons where each card's
 * inner content is provided by the host component via an `ng-template` passed through
 * `props['cardTemplate']`. The type handles all selection chrome (border ring, hover,
 * keyboard activation) — the template is responsible only for content.
 *
 * Props:
 * - `options`      – `any[]`  Array of option objects. Must each have the property
 *                             identified by `valueKey` as a unique identifier.
 * - `valueKey`     – `string` Property name on each option used as the form control value.
 *                             Defaults to `'value'`.
 * - `cardTemplate` – `TemplateRef<{ $implicit: any; selected: boolean }>`
 *                             Template that renders the interior of each card.
 *                             Template context: `let-item` (the option object),
 *                             `let-selected="selected"` (true when this card is selected).
 * - `columns`      – `2 | 3 | 4`  Visual column hint (drives a CSS class). Default: `3`.
 * - `compact`      – `boolean`  Denser sizing (shorter min-height, tighter padding/gap) for
 *                             screens where a long options list needs to fit without scrolling,
 *                             e.g. inside a mobile "peek"-height panel. Default: `false`.
 *
 * ⚠️ IMPORTANT — Because `cardTemplate` is a `TemplateRef`, the host component MUST
 * build the field config in `ngAfterViewInit` (not `ngOnInit`), after `@ViewChild`
 * has resolved the template reference.
 *
 * Usage in host component:
 * ```typescript
 * \@ViewChild('myCardTpl') myCardTplRef!: TemplateRef<{ $implicit: MyItem; selected: boolean }>;
 *
 * fields: FormlyFieldConfig[] = [];
 *
 * ngAfterViewInit(): void {
 *   this.fields = [{
 *     key: 'selectedId',
 *     type: 'card-select',
 *     props: {
 *       options: this.items,
 *       valueKey: 'id',
 *       cardTemplate: this.myCardTplRef,
 *       columns: 3,
 *     },
 *   }];
 * }
 * ```
 *
 * Usage in host template:
 * ```html
 * <ng-template #myCardTpl let-item let-selected="selected">
 *   <span>{{ item.name }}</span>
 * </ng-template>
 * ```
 */
@Component({
  selector: 'app-card-select-formly-type',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './card-select-formly-type.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './card-select-formly-type.component.css',
})
export class CardSelectFormlyTypeComponent extends FieldType<FieldTypeConfig> {
  get selectOptions(): any[] {
    return (this.props['options'] as any[]) ?? [];
  }

  get valueKey(): string {
    return (this.props['valueKey'] as string) ?? 'value';
  }

  get cardTemplate(): TemplateRef<any> | null {
    return (this.props['cardTemplate'] as TemplateRef<any>) ?? null;
  }

  get columnsClass(): string {
    const c = this.props['columns'] ?? 3;
    return `col-${c}`;
  }

  get compact(): boolean {
    return !!this.props['compact'];
  }

  isSelected(option: any): boolean {
    return option[this.valueKey] === this.formControl.value;
  }

  onSelect(option: any): void {
    this.formControl.setValue(option[this.valueKey]);
    this.formControl.markAsDirty();
  }
}
